import { Button as BaseButton, buttonVariants } from '@workspace/ui/components/button';
import * as icons from '../icon/assets';
import { cn } from '@workspace/ui/lib/utils';
import { buttonIconStyle, buttonStyles } from './button.styles';
import { Icon } from '../icon/icon';
import { LoadingDots } from '../loading-dot/loading-dot';
import { type ButtonStyleSize, type ButtonStyleType } from './types';
import { type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

type IconName = keyof typeof icons;

type BaseButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

interface ButtonProps extends BaseButtonProps {
  isLoading?: boolean;
  styleType?: ButtonStyleType;
  styleSize?: ButtonStyleSize;
  icon?: IconName;
  iconStrokeColor?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { isLoading = false, styleType = 'basic', styleSize = 'M-40', icon, className, ...props },
    ref,
  ) => {
    const buttonClasses = cn(
      buttonStyles.base,
      buttonStyles.variants[styleType],
      buttonStyles.styleSize[styleSize],
      className,
    );

    const iconColor = buttonIconStyle.color[styleType];

    const iconSizeMap = {
      'XS-24': 12,
      'S-32': 16,
      'M-40': 24,
    };

    return (
      <BaseButton ref={ref} disabled={isLoading} className={buttonClasses} {...props}>
        {isLoading ? (
          <LoadingDots />
        ) : (
          <div className='inline-flex items-center justify-center h-4 gap-1'>
            {icon && (
              <Icon
                name={icon}
                size={iconSizeMap[styleSize]}
                color={props.disabled ? '#BFBFBF' : iconColor}
              />
            )}
            {props.children}
          </div>
        )}
      </BaseButton>
    );
  },
);

Button.displayName = 'Button';
